import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Lock, LockOpen, GraduationCap, Users, UserCog, Loader2 } from 'lucide-react';
import { OrgIdCardsApi } from '@/services/org-id-cards.api';
import { OrgPortalApi } from '@/services/org.api';
import { PageHeader } from '@/components/ui/PageHeader';
import { Field, Input, Select } from '@/components/ui/Input';

/**
 * ID Card Generation — org/platform-tier "Generate ID Cards". Matches the
 * real legacy screen (survey confirmed): a Lock Settings toggle plus 3
 * separate generator sections (Student / Teacher / Non-Teaching Staff),
 * each producing a print-ready page — ONLY records with both
 * id_card_required and id_card_verified true are ever included, matching
 * legacy's exact hard filter. School-tier logins cannot reach this screen
 * at all (403 on every endpoint here, matching legacy's hasRole check).
 */
export function GenerateIdCardsPage() {
  const qc = useQueryClient();
  const { data: lockSetting } = useQuery({
    queryKey: ['org-id-cards', 'lock-setting'],
    queryFn: () => OrgIdCardsApi.getLockSetting(),
  });

  const toggleLock = useMutation({
    mutationFn: (locked: boolean) => OrgIdCardsApi.setLockSetting(locked),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['org-id-cards', 'lock-setting'] }),
  });

  const { data: schoolsPage } = useQuery({
    queryKey: ['org-schools-for-id-cards'],
    queryFn: () => OrgPortalApi.listSchools({ limit: 100 }),
  });
  const schools = schoolsPage?.items ?? [];

  return (
    <>
      <PageHeader title="Generate ID Cards" description="Bulk-print student and staff ID cards — only verified, currently-required records are included." />

      <div className="card mb-6 p-5">
        <h3 className="mb-1 text-sm font-semibold text-slate-700">ID Card Verification Lock Settings</h3>
        <p className="mb-3 text-sm text-slate-500">
          When locked, School Admins cannot save, lock, unlock, exclude, or restore ID card verification data for any of your schools.
        </p>
        <div className="flex items-center gap-3">
          <button
            className={`inline-flex items-center gap-2 rounded-md px-4 py-2 text-sm font-medium ${
              lockSetting?.locked ? 'bg-red-600 text-white' : 'bg-slate-100 text-slate-600'
            }`}
            onClick={() => toggleLock.mutate(!lockSetting?.locked)}
            disabled={toggleLock.isPending || !lockSetting}
          >
            {toggleLock.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : lockSetting?.locked ? (
              <Lock className="h-4 w-4" />
            ) : (
              <LockOpen className="h-4 w-4" />
            )}
            {lockSetting?.locked ? 'Locked' : 'Unlocked'}
          </button>
          <span className="text-xs text-slate-400">Click to {lockSetting?.locked ? 'unlock' : 'lock'} verification edits.</span>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-5 lg:grid-cols-3">
        <StudentGenerator schools={schools} />
        <StaffGenerator title="Teacher ID Cards Generator" icon={Users} onGenerate={OrgIdCardsApi.generateTeachers} schools={schools} />
        <StaffGenerator title="Non-Teaching Staff ID Cards Generator" icon={UserCog} onGenerate={OrgIdCardsApi.generateNonTeachingStaff} schools={schools} />
      </div>
    </>
  );
}

interface SchoolOption {
  id: string;
  name: string;
}

function StudentGenerator({ schools }: { schools: SchoolOption[] }) {
  const [schoolId, setSchoolId] = useState('all');
  const [classId, setClassId] = useState('');

  const { data: classes = [] } = useQuery({
    queryKey: ['org-id-cards', 'classes', schoolId],
    queryFn: () => OrgIdCardsApi.listClasses(schoolId),
    enabled: schoolId !== 'all',
  });

  const changeSchool = (id: string) => {
    setSchoolId(id);
    setClassId('');
  };

  const generate = useMutation({
    mutationFn: () => OrgIdCardsApi.generateStudents({ schoolId, classId: schoolId !== 'all' ? classId || undefined : undefined }),
  });

  return (
    <div className="card p-5">
      <h3 className="mb-3 flex items-center gap-2 text-sm font-semibold text-slate-700">
        <GraduationCap className="h-4 w-4" /> Student ID Cards Generator
      </h3>
      <div className="space-y-3">
        <Field label="School">
          <Select value={schoolId} onChange={(e) => changeSchool(e.target.value)}>
            <option value="all">All Schools</option>
            {schools.map((s) => (
              <option key={s.id} value={s.id}>
                {s.name}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="Class (optional)">
          <Select value={classId} onChange={(e) => setClassId(e.target.value)} disabled={schoolId === 'all'}>
            <option value="">All classes</option>
            {classes.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </Select>
          {schoolId === 'all' && <p className="mt-1 text-xs text-slate-400">Select a specific school to filter by class.</p>}
        </Field>
        {generate.error && <div className="rounded-md bg-red-50 px-3 py-2 text-xs text-red-700">Could not generate — please try again.</div>}
        <button className="btn-primary w-full" onClick={() => generate.mutate()} disabled={generate.isPending}>
          {generate.isPending ? 'Generating…' : 'Generate'}
        </button>
      </div>
    </div>
  );
}

function StaffGenerator({
  title,
  icon: Icon,
  onGenerate,
  schools,
}: {
  title: string;
  icon: typeof Users;
  onGenerate: (params: { schoolId?: string; joiningYear?: number }) => Promise<void>;
  schools: SchoolOption[];
}) {
  const [schoolId, setSchoolId] = useState('all');
  const [joiningYear, setJoiningYear] = useState('');

  const generate = useMutation({
    mutationFn: () => onGenerate({ schoolId, joiningYear: joiningYear ? Number(joiningYear) : undefined }),
  });

  return (
    <div className="card p-5">
      <h3 className="mb-3 flex items-center gap-2 text-sm font-semibold text-slate-700">
        <Icon className="h-4 w-4" /> {title}
      </h3>
      <div className="space-y-3">
        <Field label="School">
          <Select value={schoolId} onChange={(e) => setSchoolId(e.target.value)}>
            <option value="all">All Schools</option>
            {schools.map((s) => (
              <option key={s.id} value={s.id}>
                {s.name}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="Joining Year (optional)">
          <Input type="number" value={joiningYear} onChange={(e) => setJoiningYear(e.target.value)} placeholder="e.g. 2024" />
        </Field>
        {generate.error && <div className="rounded-md bg-red-50 px-3 py-2 text-xs text-red-700">Could not generate — please try again.</div>}
        <button className="btn-primary w-full" onClick={() => generate.mutate()} disabled={generate.isPending}>
          {generate.isPending ? 'Generating…' : 'Generate'}
        </button>
      </div>
    </div>
  );
}
